import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);       // { username, role, email, ... }
  const [token, setToken] = useState(() => localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(true); // true while restoring session on mount

  // Restore session from stored token on first mount
  useEffect(() => {
    const restore = async () => {
      const stored = localStorage.getItem('auth_token');
      if (!stored) {
        setLoading(false);
        return;
      }
      try {
        const res = await fetch('/api/auth/me', {
          headers: { Authorization: `Bearer ${stored}` },
        });
        if (res.ok) {
          const me = await res.json();
          setUser(me);
          setToken(stored);
        } else {
          // Token expired or invalid — clear it
          localStorage.removeItem('auth_token');
          setToken(null);
        }
      } catch {
        // Silent fail — backend unreachable, keep user logged out
      } finally {
        setLoading(false);
      }
    };
    restore();
  }, []);

  const login = async (username, password) => {
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);

    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: formData.toString(),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Login failed');
    }

    const data = await res.json();
    localStorage.setItem('auth_token', data.access_token);
    setToken(data.access_token);
    setUser({ username: data.username, role: data.role });
    return data;
  };

  const register = async (username, password, email) => {
    const res = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password, email: email || null, role: 'user' }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Registration failed');
    }

    return res.json();
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    setToken(null);
    setUser(null);
    // Fire-and-forget — server-side is a no-op for stateless JWT
    fetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
  };

  const isLoggedIn = !!user;
  const isAdmin = user?.role === 'admin';

  return (
    <AuthContext.Provider value={{ user, token, loading, isLoggedIn, isAdmin, login, logout, register }}>
      {children}
    </AuthContext.Provider>
  );
};
